import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Chip, Tooltip,
  CircularProgress, Alert, Switch, FormControlLabel,
  InputAdornment, TablePagination, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import axios from 'axios';
import { consultaRuc as apiConsultaRuc, consultaDni as apiConsultaDni } from '../../utils/consultaPeru';

const API = '/api/contabilidad';

// ─── COLOR PICKER simple ─────────────────────────────────────────────────────
const COLORES = ['#607d8b','#1976d2','#388e3c','#f57c00','#7b1fa2','#c62828','#00838f','#5d4037'];

// ─── FORMULARIO TIPO AUXILIAR ────────────────────────────────────────────────
function DialogTipo({ open, tipo, onClose, onSaved, empresaId }) {
  const [form, setForm] = useState({ codigo: '', descripcion: '', color: '#607d8b', activo: 1 });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (tipo) {
      setForm({ codigo: tipo.codigo, descripcion: tipo.descripcion, color: tipo.color || '#607d8b', activo: tipo.activo ?? 1 });
    } else {
      setForm({ codigo: '', descripcion: '', color: '#607d8b', activo: 1 });
    }
    setErr('');
  }, [tipo, open]);

  const guardar = async () => {
    if (!form.codigo.trim() || !form.descripcion.trim()) { setErr('Código y descripción son obligatorios'); return; }
    setSaving(true);
    try {
      if (tipo) {
        await axios.put(`${API}/auxiliar-tipos/${tipo.id}`, { ...form, empresa_id: empresaId });
      } else {
        await axios.post(`${API}/auxiliar-tipos`, { ...form, empresa_id: empresaId });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tipo ? 'Editar Tipo' : 'Nuevo Tipo de Auxiliar'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={4}>
            <TextField label="Código" value={form.codigo} onChange={e => setForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
              fullWidth size="small" inputProps={{ maxLength: 30 }} disabled={!!tipo} />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Descripción" value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Color de etiqueta</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {COLORES.map(c => (
                <Box key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                  sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                    border: form.color === c ? '3px solid #000' : '2px solid transparent' }} />
              ))}
              <Box component="input" type="color" value={form.color}
                onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                sx={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: '50%', padding: 0 }} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={
              <Switch checked={form.activo === 1} onChange={e => setForm(p => ({ ...p, activo: e.target.checked ? 1 : 0 }))} color="success" />
            } label={form.activo === 1 ? 'Activo' : 'Inactivo'} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={guardar} variant="contained" disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── FORMULARIO AUXILIAR ─────────────────────────────────────────────────────
const EMPTY_AUX = {
  tipos_ids: [],
  numero_documento: '', descripcion: '',
  nombres: '', apellidos: '', email: '', telefono: '', direccion: '', activo: 1
};

function DialogAuxiliar({ open, auxiliar, onClose, onSaved, empresaId, tipos }) {
  const [form, setForm] = useState({ ...EMPTY_AUX });
  const [saving, setSaving] = useState(false);
  const [buscandoDoc, setBuscandoDoc] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (auxiliar) {
      setForm({
        tipos_ids: auxiliar.tipos ? auxiliar.tipos.map(t => Number(t.id)) : [],
        numero_documento: auxiliar.numero_documento || '',
        descripcion: auxiliar.descripcion || '',
        nombres: auxiliar.nombres || '',
        apellidos: auxiliar.apellidos || '',
        email: auxiliar.email || '',
        telefono: auxiliar.telefono || '',
        direccion: auxiliar.direccion || '',
        activo: auxiliar.activo ?? 1,
      });
    } else {
      setForm({ ...EMPTY_AUX });
    }
    setErr('');
  }, [auxiliar, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const consultarDoc = async () => {
    const doc = form.numero_documento.trim();
    setBuscandoDoc(true); setErr('');
    try {
      if (doc.length === 11) {
        // RUC → SUNAT (endpoint especializado de contabilidad)
        const r = await axios.get(`${API}/auxiliares/consultar-ruc`, { params: { ruc: doc, empresa_id: empresaId } });
        const d = r.data;
        setForm(p => ({
          ...p,
          descripcion: d.razon_social || d.nombre || p.descripcion,
          nombres:     d.nombre || p.nombres,
          direccion:   d.direccion_completa || d.direccion || p.direccion,
        }));
      } else if (doc.length === 8) {
        // DNI → RENIEC
        const d = await apiConsultaDni(doc);
        if (!d) throw new Error('No se encontró el DNI en RENIEC');
        const nombreCompleto = [d.nombres, d.apellidoPaterno, d.apellidoMaterno].filter(Boolean).join(' ');
        setForm(p => ({
          ...p,
          descripcion: nombreCompleto || p.descripcion,
          nombres:     d.nombres || p.nombres,
          apellidos:   [d.apellidoPaterno, d.apellidoMaterno].filter(Boolean).join(' ') || p.apellidos,
        }));
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'No se pudo consultar el documento');
    } finally { setBuscandoDoc(false); }
  };

  const guardar = async () => {
    if (!form.numero_documento.trim() || !form.descripcion.trim()) {
      setErr('N° documento y descripción/razón social son obligatorios'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        empresa_id: empresaId,
        tipo: form.tipos_ids.length > 0
          ? (tipos.find(t => Number(t.id) === form.tipos_ids[0])?.codigo || '')
          : '',
      };
      if (auxiliar) {
        await axios.put(`${API}/auxiliares/${auxiliar.id}`, payload);
      } else {
        await axios.post(`${API}/auxiliares`, payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{auxiliar ? 'Editar Auxiliar' : 'Nuevo Auxiliar'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Tipos de auxiliar{' '}
              <span style={{ fontWeight: 400, color: '#999' }}>(puede marcar varios)</span>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
              {tipos.filter(t => t.activo).map(t => {
                const checked = form.tipos_ids.includes(Number(t.id));
                return (
                  <Chip key={t.id} label={t.descripcion} size="small"
                    onClick={() => {
                      const tid = Number(t.id);
                      setForm(p => ({
                        ...p,
                        tipos_ids: checked
                          ? p.tipos_ids.filter(x => x !== tid)
                          : [...p.tipos_ids, tid]
                      }));
                    }}
                    variant={checked ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer', fontSize: 12, height: 28,
                      bgcolor: checked ? (t.color || '#607d8b') : 'transparent',
                      color: checked ? '#fff' : (t.color || '#607d8b'),
                      borderColor: t.color || '#607d8b',
                      fontWeight: checked ? 700 : 400,
                      '&:hover': { opacity: 0.85 },
                    }} />
                );
              })}
              {tipos.filter(t => t.activo).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No hay tipos configurados
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="N° Documento (DNI 8 / RUC 11)" value={form.numero_documento}
              onChange={e => set('numero_documento', e.target.value.replace(/\D/g, ''))}
              fullWidth size="small"
              helperText={
                form.numero_documento.length === 8 ? 'DNI → consulta RENIEC' :
                form.numero_documento.length === 11 ? 'RUC → consulta SUNAT' : ' '
              }
              InputProps={{
                endAdornment: (form.numero_documento.length === 8 || form.numero_documento.length === 11) ? (
                  <InputAdornment position="end">
                    <Tooltip title={form.numero_documento.length === 8 ? 'Consultar DNI en RENIEC' : 'Consultar RUC en SUNAT'}>
                      <IconButton size="small" onClick={consultarDoc} disabled={buscandoDoc}>
                        {buscandoDoc ? <CircularProgress size={16} /> : <PersonSearchIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null
              }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Razón Social / Descripción" value={form.descripcion}
              onChange={e => set('descripcion', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Nombres" value={form.nombres} onChange={e => set('nombres', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Apellidos" value={form.apellidos} onChange={e => set('apellidos', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" value={form.email} onChange={e => set('email', e.target.value)} fullWidth size="small" type="email" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Teléfono" value={form.telefono} onChange={e => set('telefono', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Dirección" value={form.direccion} onChange={e => set('direccion', e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={
              <Switch checked={form.activo === 1} onChange={e => set('activo', e.target.checked ? 1 : 0)} color="success" />
            } label={form.activo === 1 ? 'Activo' : 'Inactivo'} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={guardar} variant="contained" disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── TAB: TIPOS ──────────────────────────────────────────────────────────────
function TabTipos({ empresaId }) {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState('');

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auxiliar-tipos`, { params: { empresa_id: empresaId } });
      setTipos(r.data || []);
    } catch (e) { setErr('Error al cargar tipos'); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (t) => {
    if (t.total_auxiliares > 0) { setErr(`No se puede eliminar: tiene ${t.total_auxiliares} auxiliares asociados`); return; }
    if (!window.confirm(`¿Eliminar tipo "${t.descripcion}"?`)) return;
    try {
      await axios.delete(`${API}/auxiliar-tipos/${t.id}`, { params: { empresa_id: empresaId } });
      cargar();
    } catch (e) { setErr(e.response?.data?.error || 'Error al eliminar'); }
  };

  return (
    <Box>
      {err && <Alert severity="error" onClose={() => setErr('')} sx={{ mb: 2 }}>{err}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDialogOpen(true); }}>
          Nuevo Tipo
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Color</TableCell>
              <TableCell align="center">Auxiliares</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : tipos.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>No hay tipos registrados</TableCell></TableRow>
            ) : tipos.map(t => (
              <TableRow key={t.id} hover>
                <TableCell><Chip label={t.codigo} size="small" sx={{ bgcolor: t.color || '#607d8b', color: '#fff', fontWeight: 600, fontSize: 11 }} /></TableCell>
                <TableCell>{t.descripcion}</TableCell>
                <TableCell align="center">
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: t.color || '#607d8b', mx: 'auto', border: '1px solid rgba(0,0,0,0.1)' }} />
                </TableCell>
                <TableCell align="center">{t.total_auxiliares || 0}</TableCell>
                <TableCell align="center">
                  <Chip label={t.activo ? 'Activo' : 'Inactivo'} size="small"
                    color={t.activo ? 'success' : 'default'} variant={t.activo ? 'filled' : 'outlined'} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setSelected(t); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => eliminar(t)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogTipo open={dialogOpen} tipo={selected} onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); cargar(); }} empresaId={empresaId} />
    </Box>
  );
}

// ─── TAB: AUXILIARES ─────────────────────────────────────────────────────────
function TabAuxiliares({ empresaId, empresaInfo }) {
  const [rows, setRows] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);

  const [filtros, setFiltros] = useState({ buscar: '', tipo_id: '', activo: '1' });
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const buscarRef = useRef(null);

  const cargarTipos = useCallback(async () => {
    if (!empresaId) return;
    try {
      const r = await axios.get(`${API}/auxiliar-tipos`, { params: { empresa_id: empresaId } });
      setTipos(r.data || []);
    } catch (_) {}
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auxiliares`, {
        params: { empresa_id: empresaId, ...filtros, page: page + 1, limit: rowsPerPage }
      });
      setRows(r.data.rows || r.data || []);
      setTotal(r.data.total || (r.data.rows ? r.data.total : r.data.length) || 0);
    } catch (e) { setErr('Error al cargar auxiliares'); }
    finally { setLoading(false); }
  }, [empresaId, filtros, page, rowsPerPage]);

  useEffect(() => { cargarTipos(); }, [cargarTipos]);
  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (a) => {
    if (!window.confirm(`¿Desactivar auxiliar "${a.descripcion}"?`)) return;
    try {
      await axios.delete(`${API}/auxiliares/${a.id}`, { params: { empresa_id: empresaId } });
      cargar();
    } catch (e) { setErr(e.response?.data?.error || 'Error al eliminar'); }
  };

  const sincronizar = async () => {
    if (!window.confirm('¿Importar proveedores y clientes desde el ERP? Se agregarán nuevos registros sin borrar los existentes.')) return;
    setSyncing(true); setErr(''); setSuccess('');
    try {
      const r = await axios.post(`${API}/auxiliares/sincronizar-erp`, { empresa_id: empresaId });
      setSuccess(`Sincronización completada: ${r.data.insertados} nuevos, ${r.data.actualizados} actualizados`);
      cargar();
    } catch (e) { setErr(e.response?.data?.error || 'Error en sincronización'); }
    finally { setSyncing(false); }
  };

  const tipoChip = (row) => {
    const tiposList = row.tipos && row.tipos.length > 0 ? row.tipos : [];
    if (tiposList.length > 0) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {tiposList.map(t => (
            <Chip key={t.id} label={t.descripcion} size="small"
              sx={{ bgcolor: t.color || '#607d8b', color: '#fff', fontSize: 10, height: 20 }} />
          ))}
        </Box>
      );
    }
    // Fallback a tipo legacy
    const t = tipos.find(x => x.codigo === row.tipo);
    if (!t) return row.tipo ? <Chip label={row.tipo} size="small" /> : null;
    return <Chip label={t.descripcion} size="small"
      sx={{ bgcolor: t.color || '#607d8b', color: '#fff', fontSize: 10 }} />;
  };

  return (
    <Box>
      {err && <Alert severity="error" onClose={() => setErr('')} sx={{ mb: 2 }}>{err}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filtros + acciones */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Buscar por doc, nombre..." value={filtros.buscar}
          onChange={e => { setFiltros(p => ({ ...p, buscar: e.target.value })); setPage(0); }}
          inputRef={buscarRef}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }} />
        <TextField select size="small" label="Tipo" value={filtros.tipo_id}
          onChange={e => { setFiltros(p => ({ ...p, tipo_id: e.target.value })); setPage(0); }}
          sx={{ minWidth: 170 }}>
          <MenuItem value="">Todos</MenuItem>
          {tipos.map(t => <MenuItem key={t.id} value={t.id}>{t.descripcion}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Estado" value={filtros.activo}
          onChange={e => { setFiltros(p => ({ ...p, activo: e.target.value })); setPage(0); }}
          sx={{ minWidth: 120 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="1">Activos</MenuItem>
          <MenuItem value="0">Inactivos</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        {empresaInfo?.erp_empresa_id && (
          <Tooltip title="Importar proveedores y clientes desde el ERP (modo aditivo, no borra registros)">
            <span>
              <Button variant="outlined" startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={sincronizar} disabled={syncing} size="small">
                Sincronizar ERP
              </Button>
            </span>
          </Tooltip>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDialogOpen(true); }}>
          Nuevo Auxiliar
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Tipo</TableCell>
              <TableCell>N° Documento</TableCell>
              <TableCell>Razón Social / Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>No se encontraron auxiliares</TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{tipoChip(r)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.numero_documento}</TableCell>
                <TableCell>{r.descripcion}{r.nombres ? ` — ${r.nombres} ${r.apellidos || ''}`.trim() : ''}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.email}</TableCell>
                <TableCell>{r.telefono}</TableCell>
                <TableCell align="center">
                  <Chip label={r.activo ? 'Activo' : 'Inactivo'} size="small"
                    color={r.activo ? 'success' : 'default'} variant={r.activo ? 'filled' : 'outlined'} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setSelected(r); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => eliminar(r)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]} labelRowsPerPage="Filas:" />

      <DialogAuxiliar open={dialogOpen} auxiliar={selected} onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); cargar(); }} empresaId={empresaId} tipos={tipos} />
    </Box>
  );
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────
export default function AuxiliaresContable({ empresaId: propEmpresaId }) {
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(propEmpresaId || '');
  const [empresaInfo, setEmpresaInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => {
      setEmpresas(r.data || []);
      if (!empresaId && r.data?.length > 0) setEmpresaId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (empresaId) {
      const e = empresas.find(x => x.id === Number(empresaId) || x.id === empresaId);
      setEmpresaInfo(e || null);
    }
  }, [empresaId, empresas]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <PeopleIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Auxiliares Contables</Typography>
        <TextField select size="small" label="Empresa" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: 220 }}>
          {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social || e.nombre}</MenuItem>)}
        </TextField>
      </Box>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Auxiliares" />
          <Tab icon={<CategoryIcon />} iconPosition="start" label="Tipos de Auxiliar" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && empresaId && <TabAuxiliares empresaId={empresaId} empresaInfo={empresaInfo} />}
          {tab === 1 && empresaId && <TabTipos empresaId={empresaId} />}
          {!empresaId && <Alert severity="info">Seleccione una empresa contable para gestionar auxiliares.</Alert>}
        </Box>
      </Paper>
    </Box>
  );
}
